import ComponentManifestBase from '../../../base/ComponentManifest.base';
import type { FetchManifest, IResolverClass } from '../../componentManifestResolver.types';
import type { ComponentManifestResolverConstructor } from './types';
import type { WixComponentManifest } from '../../../schema';
import ComponentManifestMerger from './ComponentManifest.merger';

class ComponentManifestResolver
  extends ComponentManifestMerger
  implements IResolverClass<WixComponentManifest>
{
  private fetchManifest: FetchManifest;
  private shouldResolveElements: boolean = false;

  constructor({ fetchManifest, manifest, resolveElements }: ComponentManifestResolverConstructor) {
    super({ manifest: { ...manifest } });
    this.fetchManifest = fetchManifest;
    this.shouldResolveElements = resolveElements || false;
  }

  public async fetchFromReference(ref: string) {
    this.referredComponentManifest = new ComponentManifestBase(await this.fetchManifest(ref));

    const fetchedManifestReferredType = this.referredComponentManifest.getReferredType();

    if (fetchedManifestReferredType) {
      // recursively resolve all referred manifests
      const resolvedFetchedManifest = await new ComponentManifestResolver({
        fetchManifest: this.fetchManifest,
        manifest: this.referredComponentManifest.getManifest()
      }).resolve();

      this.referredComponentManifest.setManifest(resolvedFetchedManifest);
    }
  }

  protected async resolveElements() {
    const elements = this.mainComponentManifest.getElements();
    for (const [elementKey, element] of Object.entries(elements || {})) {
      const elementReferredType = element.referredType;
      if (!elementReferredType) {
        continue;
      }

      const elementManifest = new ComponentManifestResolver({
        fetchManifest: this.fetchManifest,
        manifest: element,
        resolveElements: true
      });

      const resolvedElement = await elementManifest.resolve();
      this.mainComponentManifest.setElement(elementKey, resolvedElement);
    }
  }

  public async resolve() {
    const manifestReferredType = this.mainComponentManifest.getReferredType();
    if (manifestReferredType) {
      await this.fetchFromReference(manifestReferredType);
      this.mainComponentManifest.setReferredType(undefined);
    }

    const mergedManifest = super.merge();

    if (this.shouldResolveElements) {
      this.mainComponentManifest.setManifest(mergedManifest);
      await this.resolveElements();
    }

    return mergedManifest;
  }
}

export default ComponentManifestResolver;
